import { inject } from '@angular/core';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { filter, map, switchMap } from 'rxjs';
import { BreadcrumbService } from './core/services/breadcrumb.service';
import { FiltersService } from './features/admin/modules/features/filters/filters.service';
import { FormService } from './features/admin/modules/features/form-builder/form.service';
import { ColumnsService } from './features/admin/modules/features/columns/columns.service';
import { WidgetsService } from './features/admin/widgets/widgets.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { EntriesService } from './features/admin/modules/features/entries/entries.service';
import { TaxonomiesService } from './features/admin/modules/features/taxonomies/taxonomies.service';
import { ModulesStore } from './features/admin/modules/modules.store';
import { MigrateService } from './shared/services/migrate-service';
import { UserPreferenceStore } from './core/services/user-preference/user-preference.store';
import { SignalRService } from './core/services/signalr.service';
import { EmbeddedServiceService } from './core/services/embedded.service';
import { SystemSettingsStore } from './core/services/system-settings.store';
import { UserStore } from './core/services/user.store';
import { ThemeStore } from './core/services/theme.store';
import { OneSignalService } from './core/services/one-signal.service';
import { ActivatedRouteSnapshot, ResolveFn, RouterStateSnapshot } from '@angular/router';
import { SentryFeedbackService } from './core/services/sentry-feedback.service';
import { NotificationsService } from './core/layout/common/notifications/notifications.service';

export const initialDataResolver = () => {
  // initialize services
  const modulesStore = inject(ModulesStore);
  const userStore = inject(UserStore);
  userStore.ensureLoaded();
  const systemSettingsStore = inject(SystemSettingsStore);
  const taxonomiesService = inject(TaxonomiesService);
  if (!modulesStore.taxonomiesLoaded()) {
    modulesStore.loadAllTaxonomies();
  }

  const widgetsService = inject(WidgetsService);
  const filtersService = inject(FiltersService);
  const formService = inject(FormService);
  const columnsService = inject(ColumnsService);
  const migrateService = inject(MigrateService);
  const signalRService = inject(SignalRService);
  const oneSignalService = inject(OneSignalService);
  const sentryFeedbackService = inject(SentryFeedbackService);
  const notificationsService = inject(NotificationsService);

  const entriesService = inject(EntriesService);

  const themeStore = inject(ThemeStore);
  const embeddedServiceService = inject(EmbeddedServiceService);
  const breadcrumbService = inject(BreadcrumbService);
  const navigationService = inject(NavigationService);
  // initialize computed values

  const modulesLoaded = toObservable(modulesStore.modulesLoaded);
  const taxonomiesLoaded = toObservable(modulesStore.taxonomiesLoaded);
  const user = toObservable(userStore.loaded);
  const userPreferenceStore = inject(UserPreferenceStore);
  const userPreferenceLoaded = toObservable(userPreferenceStore.loaded);

  // load entries if modules are loaded
  const loaded = modulesLoaded.pipe(
    filter(value => value === true),
    switchMap(() => {
      return taxonomiesLoaded.pipe(filter(value => value === true));
    }),
    switchMap(() => {
      return user.pipe(filter(value => value === true));
    }),
    switchMap(() => {
      userPreferenceStore.load(null);
      return userPreferenceLoaded.pipe(filter(value => value === true));
    }),
    switchMap(() => {
      return entriesService.initUserEntries();
    }),
    map(() => {
      notificationsService.loadEntries(true);
      migrateService.loaded();
      return true;
    })
  );

  navigationService.get();

  return loaded;
};

/**
 * This resolver loads only the minimal data required for public modules
 * without loading authentication and user data
 */
export const publicDataResolver = () => {
  // initialize services
  const modulesStore = inject(ModulesStore);
  const systemSettingsStore = inject(SystemSettingsStore);
  const taxonomiesService = inject(TaxonomiesService);
  if (!modulesStore.taxonomiesLoaded()) {
    modulesStore.loadAllTaxonomies();
  }

  const widgetsService = inject(WidgetsService);
  const filtersService = inject(FiltersService);
  const formService = inject(FormService);
  const columnsService = inject(ColumnsService);
  const entriesService = inject(EntriesService);

  const themeStore = inject(ThemeStore);
  const embeddedServiceService = inject(EmbeddedServiceService);
  const breadcrumbService = inject(BreadcrumbService);
  const navigationService = inject(NavigationService);
  // initialize computed values

  const modulesLoaded = toObservable(modulesStore.modulesLoaded);
  const taxonomiesLoaded = toObservable(modulesStore.taxonomiesLoaded);

  // load entries if modules are loaded
  const loaded = modulesLoaded.pipe(
    filter(value => value === true),
    switchMap(() => {
      return taxonomiesLoaded.pipe(filter(value => value === true));
    }),
    map(() => {
      return true;
    })
  );

  navigationService.get();

  return loaded;
};

/**
 * Factory function that returns the appropriate resolver based on whether a module is public
 */
export function getModuleResolver(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): ResolveFn<boolean> {
  const modulesStore = inject(ModulesStore);

  // Get the first URL segment after the leading slash (moduleSlug)
  const urlSegments = state.url.split('/');
  const moduleSlug = urlSegments[1] || '';

  // Find the module based on the slug
  const module = modulesStore.getModuleBySlug(moduleSlug);

  // If the module is public, use the public resolver
  if (module?.isPublic) {
    return publicDataResolver;
  }

  // Otherwise use the standard resolver
  return initialDataResolver;
}
